/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable arrow-body-style */
/* eslint-disable consistent-return */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-restricted-syntax */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createCategoryId } from '../utils/categoryHelpers';

export const useScrollManagement = (categories) => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isNavFixed, setIsNavFixed] = useState(false);

  const navRef = useRef(null);
  const categorySectionsRef = useRef({});
  const navOriginalPositionRef = useRef(null);
  const isScrollingProgrammatically = useRef(false);
  const scrollTimeout = useRef(null);

  // Separate RAF refs to avoid platform-switching collisions
  const mobileRafRef = useRef(null);
  const desktopRafRef = useRef(null);

  // Stateful ref for the desktop container
  const [container, setContainer] = useState(null);

  /**
   * Hybrid Ref: acts as both a callback ref and a ref object.
   */
  const scrollableContentRef = useMemo(() => {
    const fn = (node) => {
      if (node !== null) {
        setContainer(node);
      }
      fn.current = node;
    };
    fn.current = null;
    return fn;
  }, []);

  // ─── Active Category Detection ──────────────────────────────────────────────
  const detectActiveCategory = useCallback(
    (containerEl) => {
      // Don't update highlight if we are in the middle of a smooth scroll triggered by the nav
      if (isScrollingProgrammatically.current) return;
      if (categories.length === 0) return;

      const isMobile = window.innerWidth < 768;

      // On mobile we measure relative to viewport (0), on desktop relative to container top
      const refTop = containerEl ? containerEl.getBoundingClientRect().top : 0;

      // The offset threshold from the reference top
      const OFFSET = isMobile ? 120 : 110;

      let activeCandidate = null;
      let highestDistanceBelowThreshold = -Infinity;

      categories.forEach((cat) => {
        const prefix = isMobile ? 'mobile-category' : 'category';
        const id = createCategoryId(cat.name, prefix);
        const el = document.getElementById(id);
        if (!el) return;

        const elTop = el.getBoundingClientRect().top;
        const relativeTop = elTop - refTop;
        const distance = relativeTop - OFFSET;

        // Condition: The element has reached or scrolled past the threshold line (distance <= 0)
        // We want the one whose top is closest to the threshold line (highest distance <= 0)
        if (distance <= 0) {
          if (distance > highestDistanceBelowThreshold) {
            highestDistanceBelowThreshold = distance;
            activeCandidate = cat.name;
          }
        }
      });

      // Default to first category if we are near the top or nothing matches yet
      if (!activeCandidate && categories.length > 0) {
        // Only force first category if we're not scrolled deep (extra safety)
        const scrollVal = containerEl ? containerEl.scrollTop : window.scrollY;
        if (scrollVal < 500) {
          activeCandidate = categories[0].name;
        }
      }

      if (activeCandidate) {
        // Functional update to avoid stale closures
        setSelectedCategory((prev) => {
          if (prev !== activeCandidate) return activeCandidate;
          return prev;
        });
      }
    },
    [categories]
  );

  // ─── Mobile Listener ────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth >= 768) return;
    if (categories.length === 0) return;

    const onScroll = () => {
      if (mobileRafRef.current) return;
      mobileRafRef.current = requestAnimationFrame(() => {
        mobileRafRef.current = null;
        setShowScrollButton(window.scrollY > 400);
        detectActiveCategory(null);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Initial sync with a small delay to ensure DOM is ready
    const timer = setTimeout(onScroll, 200);

    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
      if (mobileRafRef.current) cancelAnimationFrame(mobileRafRef.current);
    };
  }, [categories, detectActiveCategory]);

  // ─── Desktop Listener ───────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth < 768) return;
    if (!container || categories.length === 0) return;

    const onScroll = () => {
      if (desktopRafRef.current) return;
      desktopRafRef.current = requestAnimationFrame(() => {
        desktopRafRef.current = null;
        setShowScrollButton(container.scrollTop > 400);
        detectActiveCategory(container);
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });

    const timer = setTimeout(onScroll, 200);

    return () => {
      container.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
      if (desktopRafRef.current) cancelAnimationFrame(desktopRafRef.current);
    };
  }, [categories, container, detectActiveCategory]);

  // ─── Compatibility Handlers ──────────────────────────────────────────────────
  const handleMobileScroll = useCallback(() => { }, []);
  const handleDesktopScroll = useCallback(() => { }, []);

  // ─── Actions ────────────────────────────────────────────────────────────────
  const scrollToTop = useCallback(() => {
    isScrollingProgrammatically.current = true;
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, 1000);
  }, [container]);

  const setCategoryRef = useCallback((category, ref) => {
    if (ref) {
      categorySectionsRef.current[category] = ref;
    }
  }, []);

  // ─── Initial Setup ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (navRef.current) {
      const navPosition =
        navRef.current.getBoundingClientRect().top + window.scrollY;
      navOriginalPositionRef.current = navPosition;
    }
  }, []);

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0].name);
    }
  }, [categories, selectedCategory]);

  // Final Cleanup
  useEffect(() => {
    return () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      if (mobileRafRef.current) cancelAnimationFrame(mobileRafRef.current);
      if (desktopRafRef.current) cancelAnimationFrame(desktopRafRef.current);
    };
  }, []);

  return {
    showScrollButton,
    selectedCategory,
    setSelectedCategory,
    isNavFixed,
    navRef,
    categorySectionsRef,
    scrollableContentRef,
    isScrollingProgrammatically,
    scrollTimeout,
    handleMobileScroll,
    handleDesktopScroll,
    scrollToTop,
    setCategoryRef
  };
};
